// src/components/tags/TagForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createTag,
  updateTag,
  fetchTag,
  clearTagError,
  clearTagSuccess,
} from '../../store/actions/tagActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';

const TagForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { selectedTag, loading, error, success } = useSelector(
    (state) => state.tags
  );

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    type: 'GENERAL',
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = !!id;

  const typeOptions = [
    { value: 'GENERAL', label: 'Général', description: 'Usage général' },
    { value: 'FAQ', label: 'FAQ', description: 'Pour les questions fréquentes' },
    { value: 'BLOG', label: 'Blog', description: 'Pour les articles de blog' },
    { value: 'TOPIC', label: 'Sujet', description: 'Sujet de discussion' },
    { value: 'SKILL', label: 'Compétence', description: 'Compétence ou expertise' },
  ];

  // Styles object
  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem 1rem',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#111827',
      margin: 0,
    },
    cancelButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    cancelButtonHover: {
      backgroundColor: '#f9fafb',
    },
    form: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      padding: '2rem',
    },
    section: {
      marginBottom: '2rem',
      paddingBottom: '2rem',
      borderBottom: '1px solid #e5e7eb',
    },
    sectionTitle: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '1.5rem',
    },
    fieldGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
      marginBottom: '0.5rem',
    },
    input: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    textarea: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      minHeight: '100px',
      resize: 'vertical',
    },
    select: {
      width: '100%',
      padding: '0.625rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      backgroundColor: '#ffffff',
    },
    inputError: {
      borderColor: '#dc2626',
    },
    errorText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#dc2626',
    },
    helperText: {
      marginTop: '0.25rem',
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    slugPreview: {
      fontSize: '0.875rem',
      color: '#6b7280',
      marginTop: '0.25rem',
    },
    typeCards: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem',
      marginTop: '0.5rem',
    },
    typeCard: {
      padding: '1rem',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    typeCardHover: {
      borderColor: '#2563eb',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    },
    typeCardSelected: {
      borderColor: '#2563eb',
      backgroundColor: '#eff6ff',
    },
    typeCardTitle: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#111827',
      marginBottom: '0.25rem',
    },
    typeCardDescription: {
      fontSize: '0.75rem',
      color: '#6b7280',
    },
    checkbox: {
      width: '1rem',
      height: '1rem',
      marginRight: '0.5rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      marginTop: '2rem',
    },
    submitButton: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    submitButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
  };

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchTag(id));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (isEditMode && selectedTag) {
      setFormData({
        name: selectedTag.name || '',
        slug: selectedTag.slug || '',
        description: selectedTag.description || '',
        type: selectedTag.type || 'GENERAL',
        isActive: selectedTag.isActive ?? true,
      });
    }
  }, [selectedTag, isEditMode]);

  // Generate slug from name
  useEffect(() => {
    if (!isEditMode && formData.name && !formData.slug) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-');
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.name, isEditMode]);

  // Cleanup
  useEffect(() => {
    return () => {
      dispatch(clearTagError());
      dispatch(clearTagSuccess());
    };
  }, [dispatch]);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Le slug est requis';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Le slug ne peut contenir que des lettres minuscules, des chiffres et des tirets';
    }

    if (!formData.type) {
      newErrors.type = 'Le type est requis';
    }

    return newErrors;
  }, [formData]);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  }, [errors]);

  const handleTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, type }));
    if (errors.type) {
      setErrors(prev => ({ ...prev, type: undefined }));
    }
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditMode) {
        await dispatch(updateTag(id, formData));
      } else {
        await dispatch(createTag(formData));
      }
      navigate('/admin/tags');
    } catch (error) {
      console.error('Form submission failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, formData, id, isEditMode, navigate, validateForm]);

  const handleCancel = useCallback(() => {
    const hasChanges = Object.keys(formData).some(key => {
      return formData[key] !== '' && formData[key] !== 'GENERAL' && formData[key] !== true;
    });

    if (hasChanges) {
      setShowCancelConfirm(true);
    } else {
      navigate('/admin/tags');
    }
  }, [formData, navigate]);

  if (loading && isEditMode) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>
          {isEditMode ? 'Modifier le tag' : 'Créer un nouveau tag'}
        </h1>
        <button
          onClick={handleCancel}
          style={styles.cancelButton}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButtonHover.backgroundColor;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = styles.cancelButton.backgroundColor;
          }}
        >
          Annuler
        </button>
      </div>

      {error && (
        <Alert
          type="error"
          message={error}
          onClose={() => dispatch(clearTagError())}
        />
      )}

      {success && (
        <Alert
          type="success"
          message={success}
          onClose={() => dispatch(clearTagSuccess())}
        />
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basic Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Informations de base</h2>
          
          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="name">
              Nom *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={{
                ...styles.input,
                ...(errors.name ? styles.inputError : {}),
              }}
              placeholder="Nom du tag"
            />
            {errors.name && (
              <div style={styles.errorText}>{errors.name}</div>
            )}
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="slug">
              Slug *
            </label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              style={{
                ...styles.input,
                ...(errors.slug ? styles.inputError : {}),
              }}
              placeholder="nom-du-tag"
            />
            {errors.slug && (
              <div style={styles.errorText}>{errors.slug}</div>
            )}
            <div style={styles.slugPreview}>
              URL: /tag/{formData.slug || 'nom-du-tag'}
            </div>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label} htmlFor="description">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              style={styles.textarea}
              placeholder="Description du tag (optionnel)"
              rows="3"
            />
            <div style={styles.helperText}>
              Une brève description pour expliquer l'utilisation de ce tag
            </div>
          </div>
        </div>

        {/* Type Selection */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Type de tag *</h2>
          {errors.type && (
            <div style={styles.errorText}>{errors.type}</div>
          )}
          
          <div style={styles.typeCards}>
            {typeOptions.map((option) => (
              <div
                key={option.value}
                style={{
                  ...styles.typeCard,
                  ...(formData.type === option.value ? styles.typeCardSelected : {}),
                }}
                onMouseEnter={(e) => {
                  if (formData.type !== option.value) {
                    e.currentTarget.style.borderColor = styles.typeCardHover.borderColor;
                    e.currentTarget.style.boxShadow = styles.typeCardHover.boxShadow;
                  }
                }}
                onMouseLeave={(e) => {
                  if (formData.type !== option.value) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
                onClick={() => handleTypeSelect(option.value)}
              >
                <div style={styles.typeCardTitle}>{option.label}</div>
                <div style={styles.typeCardDescription}>{option.description}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Status */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Statut</h2>
          
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              style={styles.checkbox}
            />
            Tag actif
          </label>
          <div style={styles.helperText}>
            Un tag inactif n'apparaîtra pas dans les suggestions
          </div>
        </div>

        {/* Form Actions */}
        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleCancel}
            style={styles.cancelButton}
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              ...styles.submitButton,
              ...(isSubmitting ? styles.submitButtonDisabled : {}),
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButtonHover.backgroundColor;
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.backgroundColor = styles.submitButton.backgroundColor;
              }
            }}
          >
            {isSubmitting 
              ? (isEditMode ? 'Modification...' : 'Création...') 
              : (isEditMode ? 'Mettre à jour' : 'Créer le tag')}
          </button>
        </div>
      </form>

      {/* Cancel Confirmation */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => navigate('/admin/tags')}
        title="Annuler les modifications"
        message="Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?"
        confirmText="Quitter"
        cancelText="Continuer l'édition"
        type="warning"
      />
    </div>
  );
};

export default TagForm;